import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

let activeSourceId = null
const SPEECH_PITCH = 0.82

const isSafariBrowser = () => {
  const ua = navigator && navigator.userAgent ? navigator.userAgent : ''
  return /Safari/.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR/.test(ua)
}

const getSpeechRate = (lang) => {
  const safari = isSafariBrowser()
  if (lang === 'es') return safari ? 0.5 : 0.8
  return safari ? 0.55 : 0.83
}
const PAUSE_DELAY_SYMBOL = 80
const PAUSE_DELAY_SENTENCE = 150
const PAUSE_DELAY_PARAGRAPH = 200

const FEMALE_VOICE_KEYWORDS = [
  'zira',
  'jenny',
  'aria',
  'sara',
  'helena',
  'sabina',
  'cortana',
  'clara',
  'elsa',
  'samantha',
  'victoria',
  'karen',
  'moira',
  'fiona',
  'tessa',
  'agnes',
  'ava',
  'allison',
  'nicky',
  'kate',
  'susan',
  'emily',
  'hazel',
  'linda',
  'catherine',
  'dalia',
  'elvira',
  'lucia',
  'paulina',
  'female',
  'google us english',
  'google uk english female',
]

const isFemaleVoice = (voice) => {
  if (!voice || !voice.name) return false
  const name = voice.name.toLowerCase()
  return FEMALE_VOICE_KEYWORDS.some((kw) => name.includes(kw))
}

const getPreferredVoice = (langHint = 'en') => {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return null
  const voices = window.speechSynthesis.getVoices()
  console.log('voices', voices)
  if (!voices || !voices.length) return null

  const findByName = (name) =>
    voices.find((v) => v.name && v.name.toLowerCase().includes(name) && !isFemaleVoice(v))

  const findByLang = (prefix) =>
    voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(prefix) && !isFemaleVoice(v))

  if (langHint === 'es') {
    return (
      findByName('google español') ||
      findByName('google espanol') ||
      findByName('jorge') ||
      findByName('chrome os español 3') ||
      findByName('chrome os espanol 3') ||
      findByName('juan') ||
      findByName('chrome os español 4') ||
      findByName('chrome os espanol 4') ||
      findByLang('es-us') ||
      findByLang('es-mx') ||
      findByLang('es') ||
      null
    )
  }

  return (
    findByName('microsoft david') ||
    findByName('alex') ||
    findByName('chrome os us english 4') ||
    findByName('microsoft mark') ||
    findByName('chrome os us english 5') ||
    findByLang('en-us') ||
    findByLang('en') ||
    null
  )
}

const sanitizeText = (text) => {
  if (!text) return ''
  let result = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/&nbsp;/gi, ' ')
  result = result
    .split('\n')
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')
  return result.trim()
}

const convertRomanNumerals = (text) => {
  if (!text) return ''
  // Only convert roman numerals followed by a period (list-item context).
  // Bare patterns like \bi\b collide with real words ("I", "v", "x").
  // Longest-first ordering prevents partial-match issues.
  return text
    .replace(/\bviii\.\s*/g, 'eight. ')
    .replace(/\bvii\.\s*/g, 'seven. ')
    .replace(/\biii\.\s*/g, 'three. ')
    .replace(/\bvi\.\s*/g, 'six. ')
    .replace(/\biv\.\s*/g, 'four. ')
    .replace(/\bix\.\s*/g, 'nine. ')
    .replace(/\bii\.\s*/g, 'two. ')
    .replace(/\bv\.\s*/g, 'five. ')
    .replace(/\bx\.\s*/g, 'ten. ')
    .replace(/\bi\.\s*/g, 'one. ')
}

const expandOptionalSuffixes = (text) => {
  if (!text) return ''
  // Handle word(s) pattern - expand to plural
  return text
    .replace(/\b(\w+)\(s\)/gi, '$1s')
    .replace(/\b(\w+)\(es\)/gi, '$1es')
    .replace(/\b(\w+)\(ies\)/gi, '$1ies')
}

const removeUnnecessarySymbols = (text) => {
  if (!text) return ''
  return text
    .replace(/\*+/g, '')
    .replace(/\(a\)/gi, '')
    .replace(/\(o\)/gi, '')
    .replace(/\(as\)/gi, '')
    .replace(/\(os\)/gi, '')
    .replace(/\.{2,}/g, ' ')
    .replace(/[•◦▪]/g, '')
    .replace(/[-–—]{2,}/g, ' ')
    .replace(/[_]{2,}/g, ' ')
    .replace(/\|+/g, ' ')
    .replace(/\/+/g, ' or ')
    .replace(/\s+/g, ' ')
    .trim()
}

const splitIntoSegments = (text) => {
  if (!text) return []
  const segments = []
  let current = ''
  let inTag = false

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]

    if (ch === '<') {
      if (current.trim()) {
        segments.push({ text: current.trim(), pauseType: 'sentence' })
        current = ''
      }
      inTag = true
      continue
    }

    if (ch === '>') {
      inTag = false

      const tagStart = Math.max(0, i - 10)
      const recentText = text.substring(tagStart, i + 1)

      if (recentText.includes('</div>') || recentText.includes('</label>')) {
        segments.push({ text: '', pauseType: 'paragraph' })
      }
      continue
    }

    if (inTag) continue

    current += ch

    if (/[()[\]{}\/|\\–—]/.test(ch)) {
      const segmentText = current.slice(0, -1).trim()
      if (segmentText && !/^[\s*•◦▪_–—|\\\/]+$/.test(segmentText)) {
        segments.push({ text: segmentText, pauseType: 'symbol' })
      }
      current = ''
    }

    if (ch === '.' && current.length > 1) {
      const prevChar = current[current.length - 2]
      const nextChar = text[i + 1] || ''

      if (/[a-zA-Z]/.test(prevChar) && nextChar !== ' ' && nextChar !== '\n') {
        continue
      }

      const segmentText = current.trim()
      if (segmentText && !/^[\s*•◦▪_–—|\\\/]+$/.test(segmentText)) {
        segments.push({ text: segmentText, pauseType: 'sentence' })
      }
      current = ''
    } else if (/[!?]/.test(ch)) {
      const segmentText = current.trim()
      if (segmentText && !/^[\s*•◦▪_–—|\\\/]+$/.test(segmentText)) {
        segments.push({ text: segmentText, pauseType: 'sentence' })
      }
      current = ''
    } else if (ch === '\n') {
      const segmentText = current.trim()
      if (segmentText && !/^[\s*•◦▪_–—|\\\/]+$/.test(segmentText)) {
        segments.push({ text: segmentText, pauseType: 'paragraph' })
      }
      current = ''
    }
  }

  if (current.trim() && !/^[\s*•◦▪_–—|\\\/]+$/.test(current.trim())) {
    segments.push({ text: current.trim(), pauseType: 'sentence' })
  }
  return segments.filter((s) => s.text)
}

const extractSpeakableText = (rootEl) => {
  if (!rootEl) return ''
  const clone = rootEl.cloneNode(true)
  clone
    .querySelectorAll(
      '.rs_skip, .rsbtn, a.rsbtn_play, button, script, style, nav, input, textarea, select, [contenteditable="true"], .ws-errormessage, ' +
        'header, footer, .navbar, .nav, .menu, .navigation, .breadcrumb, .pagination, .btn, .btn-primary, .btn-secondary, ' +
        'a[href], .link, .dropdown, .modal, .popup, .tooltip, .alert, .notification, .badge, .tag',
    )
    .forEach((el) => el.remove())
  return clone.innerHTML || ''
}

const getDelayForPause = (pauseType) => {
  if (pauseType === 'symbol') return PAUSE_DELAY_SYMBOL
  if (pauseType === 'paragraph') return PAUSE_DELAY_PARAGRAPH
  return PAUSE_DELAY_SENTENCE
}

const stopSpeech = (setState, speechRef) => {
  activeSourceId = null
  setState({ isPlaying: false, isPaused: false })
  speechRef.current = null
}

const speakSegmentAtIndex = (index, sourceId, setState, speechRef) => {
  if (!speechRef.current || index >= speechRef.current.segments.length) {
    stopSpeech(setState, speechRef)
    return
  }
  if (activeSourceId !== sourceId || speechRef.current.isPaused) return

  const seg = speechRef.current.segments[index]
  const synth = window.speechSynthesis
  const utterance = new SpeechSynthesisUtterance(seg.text)
  if (speechRef.current.voice) utterance.voice = speechRef.current.voice
  utterance.lang = speechRef.current.utteranceLang
  utterance.rate = getSpeechRate(speechRef.current.detectedLang)
  utterance.pitch = SPEECH_PITCH

  utterance.onend = () => {
    if (activeSourceId !== sourceId || speechRef.current?.isPaused) return
    setTimeout(() => {
      if (speechRef.current && !speechRef.current.isPaused) {
        speechRef.current.currentIndex++
        speakSegmentAtIndex(speechRef.current.currentIndex, sourceId, setState, speechRef)
      }
    }, getDelayForPause(seg.pauseType))
  }

  utterance.onerror = () => {
    stopSpeech(setState, speechRef)
  }

  synth.speak(utterance)
}

const speak = (text, sourceId, setState, speechRef, appLanguage) => {
  console.log(speechRef)
  let cleanedText = convertRomanNumerals(
    removeUnnecessarySymbols(expandOptionalSuffixes(sanitizeText(text))),
  )
  const segments = splitIntoSegments(cleanedText)
  if (!segments.length) return

  const synth = window.speechSynthesis
  synth.cancel()
  activeSourceId = sourceId
  setState({ isPlaying: true, isPaused: false })

  const utteranceLang = appLanguage === '2' ? 'es-ES' : 'en-US'
  const langHint = appLanguage === '2' ? 'es' : 'en'
  speechRef.current = {
    segments,
    currentIndex: 0,
    isPaused: false,
    detectedLang: langHint,
    utteranceLang,
    voice: getPreferredVoice(langHint),
  }

  console.log(speechRef.current)
  speakSegmentAtIndex(0, sourceId, setState, speechRef)
}

const pauseSpeech = (setState, speechRef) => {
  if (speechRef.current) {
    speechRef.current.isPaused = true
    speechRef.current.pauseTime = Date.now()
    setState({ isPlaying: false, isPaused: true })
    window.speechSynthesis.cancel()
  }
}

const resumeSpeech = (setState, speechRef, sourceId) => {
  if (speechRef.current && speechRef.current.isPaused) {
    speechRef.current.isPaused = false
    setState({ isPlaying: true, isPaused: false })

    const timeSincePause = Date.now() - (speechRef.current.pauseTime || 0)
    // If paused briefly, replay the current segment; otherwise skip to next
    const index =
      timeSincePause < 2000 ? speechRef.current.currentIndex : ++speechRef.current.currentIndex

    speakSegmentAtIndex(index, sourceId, setState, speechRef)
  }
}

const ReadSpeakerButton = ({ id }) => {
  const language = useSelector((state) => state.language)
  const sourceIdRef = useRef(`rs-source-${Math.random().toString(36).slice(2)}`)
  const sourceId = sourceIdRef.current
  const [state, setState] = useState({ isPlaying: false, isPaused: false })
  const speechRef = useRef(null)
  const [voicesLoaded, setVoicesLoaded] = useState(false)

  useEffect(() => {
    const loadVoices = () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.getVoices()
        setVoicesLoaded(true)
      }
    }

    loadVoices()
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = loadVoices
    }

    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.onvoiceschanged = null
      }
    }
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeSourceId !== sourceId && state.isPlaying) {
        setState({ isPlaying: false, isPaused: false })
        speechRef.current = null
      }
    }, 100)
    return () => clearInterval(interval)
  }, [sourceId, state.isPlaying])

  useEffect(() => {
    return () => {
      if (activeSourceId === sourceId && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel()
        activeSourceId = null
        speechRef.current = null
      }
    }
  }, [sourceId])

  const handleClick = (e) => {
    e.preventDefault()
    if (!('speechSynthesis' in window)) {
      return
    }

    if (!voicesLoaded) {
      return
    }

    const synth = window.speechSynthesis

    if (activeSourceId === sourceId) {
      if (state.isPlaying) {
        pauseSpeech(setState, speechRef)
        return
      }
      if (state.isPaused) {
        resumeSpeech(setState, speechRef, sourceId)
        return
      }
    }

    if (synth.speaking || synth.paused) {
      synth.cancel()
      activeSourceId = null
    }

    let text = ''
    if (id) {
      const el = document.getElementById(id)
      if (el) {
        text = el.innerText || el.textContent || ''
      }
    }
    if (!text.trim()) {
      const btn = e.currentTarget
      const container = btn.closest(
        '#readspkid, #readspkid1, #readspkid2, .lblgroup, .col-lg-10, .col-md-10, .col-md-5, .col-sm-12',
      )
      text = extractSpeakableText(container)
    }
    if (!text.trim()) {
      return
    }

    speak(text, sourceId, setState, speechRef, language)
  }

  return (
    <button className="btn btn-readspeaker rs_skip" onClick={handleClick}>
      <i className={`fas ${state.isPlaying ? 'fa-pause' : 'fa-play'} me-2`} />
      {state.isPlaying ? 'Pause' : state.isPaused ? 'Resume' : 'Listen'}
    </button>
  )
}

ReadSpeakerButton.propTypes = { id: PropTypes.string }

export default ReadSpeakerButton
